/**
 * Agentic trajectory mapping model for the wizard Data Collection step
 * ("Does your data contains trajectories? = Yes", Figma 3966-23055 /
 * 4012-19043). Pure helpers over the per-target `details` object; the
 * component layer lives in `TrajectoryFieldsMapper.jsx`.
 *
 * A trajectory is a `messages`-style array of message objects. The user maps
 * the array column, the role / content / tool_call keys inside each message
 * and every distinct role value onto the canonical `user | assistant | tool`.
 */

/** Canonical roles the backend understands (`CANONICAL_TRAJECTORY_ROLES`). */
export const TRAJECTORY_ROLE_TARGETS = ['user', 'assistant', 'tool'];

/** Upload-entry sample rows read to pre-pick the trajectory keys / role values. */
export const TRAJECTORY_SAMPLE_ROWS = 20;

/** Default names probed when a trajectory column / message key is auto-picked. */
const DEFAULT_TRAJECTORY_COLUMN = 'messages';
const DEFAULT_ROLE_KEYS = ['role'];
const DEFAULT_CONTENT_KEYS = ['content'];
const DEFAULT_TOOL_CALL_KEYS = ['tool_call', 'tool_calls'];
const DEFAULT_TOOL_CALL_NAME_KEYS = ['name'];
const DEFAULT_TOOL_CALL_ARGUMENTS_KEYS = ['arguments', 'args'];

/**
 * Trajectory fields of an `emptyDetails()` object (all off / unset) — also
 * the reset applied when the user answers No. Agentic uploads default to Yes
 * once the file's columns are known, via `trajectoryYesDetails`.
 */
export const emptyTrajectoryDetails = () => ({
    /** "Does your data contains trajectories?" — agentic tasks only. */
    hasTrajectories: false,
    /** Column holding the message array (`messages`). null = unset. */
    trajectoryColumn: null,
    /** Message-object keys. null = unset; tool call is optional ('' = none). */
    trajectoryRoleKey: null,
    trajectoryContentKey: null,
    trajectoryToolCallKey: null,
    /** Keys inside each tool-call object holding the tool name / arguments (Map Tool Call Keys). */
    trajectoryToolCallNameKey: null,
    trajectoryToolCallArgumentsKey: null,
    /** Union of tool-call object keys found in the sample rows. */
    trajectoryToolCallKeys: [],
    /** Distinct raw role values found in the file (sample rows, then full scan). */
    trajectoryRoleValues: [],
    /** raw role value → 'user' | 'assistant' | 'tool' | null (cleared by the user). */
    trajectoryRoleMapping: {},
    /** Union of message-object keys, for the role / content / tool_call selects. */
    trajectoryMessageKeys: [],
    /** Whole-file role scan (`get-trajectory-role-values`) in flight. */
    trajectoryRolesLoading: false,
    /** Role scan failure, shown under the mapping rows. */
    trajectoryError: null,
    /**
     * How empty trajectory cells are imported ('unlabeled' | 'generate_with_ai'
     * | 'exclude'); null = not chosen yet (defaults to 'unlabeled' on submit).
     */
    emptyTrajectoryHandling: null,
});

const isPlainObject = (value) =>
    Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const findCaseInsensitive = (candidates, names) => {
    const lowered = (names || []).map((name) => String(name).toLowerCase());
    for (const candidate of candidates) {
        const index = lowered.indexOf(candidate.toLowerCase());
        if (index >= 0) return names[index];
    }
    return null;
};

/** `messages` when the file has such a column (any case), else null. */
export function defaultTrajectoryColumn(columns) {
    return findCaseInsensitive([DEFAULT_TRAJECTORY_COLUMN], columns || []);
}

/**
 * The message objects found under `column` across the given rows. Accepts
 * arrays of objects and JSON strings that decode to one (parquet / CSV
 * exports often stringify nested cells).
 */
export function messagesFromRows(rows, column) {
    if (!column) return [];
    const messages = [];
    for (const row of rows || []) {
        if (!isPlainObject(row)) continue;
        let value = row[column];
        if (typeof value === 'string') {
            try {
                value = JSON.parse(value);
            } catch {
                continue;
            }
        }
        if (!Array.isArray(value)) continue;
        for (const message of value) {
            if (isPlainObject(message)) messages.push(message);
        }
    }
    return messages;
}

/** Union of message-object keys under `column`, in first-seen order. */
export function messageKeysFromRows(rows, column) {
    const keys = [];
    const seen = new Set();
    for (const message of messagesFromRows(rows, column)) {
        for (const key of Object.keys(message)) {
            if (seen.has(key)) continue;
            seen.add(key);
            keys.push(key);
        }
    }
    return keys;
}

const roleValueToString = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value.trim() || null;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return null;
};

/** Distinct role values under `<column>[*].<roleKey>`, in first-seen order. */
export function roleValuesFromRows(rows, column, roleKey) {
    if (!roleKey) return [];
    const values = [];
    const seen = new Set();
    for (const message of messagesFromRows(rows, column)) {
        const value = roleValueToString(message[roleKey]);
        if (value === null || seen.has(value)) continue;
        seen.add(value);
        values.push(value);
    }
    return values;
}

/** Default role / content / tool_call keys given the message keys of a file. */
export function defaultTrajectoryKeys(messageKeys) {
    return {
        trajectoryRoleKey: findCaseInsensitive(DEFAULT_ROLE_KEYS, messageKeys),
        trajectoryContentKey: findCaseInsensitive(DEFAULT_CONTENT_KEYS, messageKeys),
        trajectoryToolCallKey: findCaseInsensitive(DEFAULT_TOOL_CALL_KEYS, messageKeys),
    };
}

const parseJsonMaybe = (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
};

/** Compact single-line text for any cell value (objects / arrays as JSON). */
export const valueToText = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

/**
 * Tool-call objects found under `<column>[*].<toolCallKey>` across the sample
 * rows. Mirrors the backend `normalize_tool_call` shapes: an object, the same
 * object as a JSON string, a list of either, and OpenAI-style
 * `{"function": {"name", "arguments"}}` wrappers (flattened so the user maps
 * `name` / `arguments` directly). Plain-string cells (a bare tool name) are
 * not objects and are skipped.
 */
export function toolCallObjectsFromRows(rows, column, toolCallKey) {
    if (!toolCallKey) return [];
    const objects = [];
    for (const message of messagesFromRows(rows, column)) {
        const cell = parseJsonMaybe(message[toolCallKey]);
        if (cell === null || cell === undefined) continue;
        const items = Array.isArray(cell) ? cell : [cell];
        for (const item of items) {
            let toolCall = parseJsonMaybe(item);
            if (!isPlainObject(toolCall)) continue;
            const fn = toolCall.function;
            if (isPlainObject(fn) && !('name' in toolCall)) {
                const rest = { ...toolCall };
                delete rest.function;
                toolCall = { ...rest, ...fn };
            }
            if (Object.keys(toolCall).length) objects.push(toolCall);
        }
    }
    return objects;
}

/** Union of tool-call object keys under `<column>[*].<toolCallKey>`, first-seen order. */
export function toolCallKeysFromRows(rows, column, toolCallKey) {
    const keys = [];
    const seen = new Set();
    for (const toolCall of toolCallObjectsFromRows(rows, column, toolCallKey)) {
        for (const key of Object.keys(toolCall)) {
            if (seen.has(key)) continue;
            seen.add(key);
            keys.push(key);
        }
    }
    return keys;
}

/** Default tool name / arguments keys given the tool-call object keys of a file. */
export function defaultToolCallKeys(toolCallKeys) {
    return {
        trajectoryToolCallNameKey: findCaseInsensitive(DEFAULT_TOOL_CALL_NAME_KEYS, toolCallKeys),
        trajectoryToolCallArgumentsKey: findCaseInsensitive(
            DEFAULT_TOOL_CALL_ARGUMENTS_KEYS,
            toolCallKeys
        ),
    };
}

/**
 * Tool-call keys block for a (new) tool-call field: the keys found in the
 * sample plus their defaults. Empty / unset when there is no tool-call field.
 */
export function toolCallKeysDetails(rows, column, toolCallKey) {
    const keys = toolCallKey ? toolCallKeysFromRows(rows, column, toolCallKey) : [];
    return { trajectoryToolCallKeys: keys, ...defaultToolCallKeys(keys) };
}

/**
 * Trajectory details for "Does your data contains trajectories? = Yes" — the
 * default for agentic uploads and the patch applied when the user answers
 * Yes: the `messages` column when the file has one, its role / content /
 * tool_call keys and the tool name / arguments keys pre-picked from the
 * sample rows (Figma 4012:19043). Everything left unset stays unset.
 */
export function trajectoryYesDetails(rows, columns) {
    const column = defaultTrajectoryColumn(columns);
    const keys = column ? messageKeysFromRows(rows, column) : [];
    const defaults = defaultTrajectoryKeys(keys);
    return {
        ...emptyTrajectoryDetails(),
        hasTrajectories: true,
        trajectoryColumn: column,
        trajectoryMessageKeys: keys,
        ...defaults,
        ...toolCallKeysDetails(rows, column, defaults.trajectoryToolCallKey),
    };
}

/** One preview text per sample row: the row's trajectory cell as compact JSON. */
export function messagesExampleSamples(rows, column, { max = 20 } = {}) {
    if (!column) return [];
    const samples = [];
    for (const row of rows || []) {
        if (!isPlainObject(row)) continue;
        const value = parseJsonMaybe(row[column]);
        if (!Array.isArray(value) || !value.length) continue;
        samples.push(JSON.stringify(value));
        if (samples.length >= max) break;
    }
    return samples;
}

/** Distinct non-empty values of `<column>[*].<key>` as text, first-seen order. */
export function messageKeySamples(rows, column, key, { max = 20 } = {}) {
    if (!key) return [];
    const samples = [];
    const seen = new Set();
    for (const message of messagesFromRows(rows, column)) {
        if (!(key in message)) continue;
        const text = valueToText(message[key]);
        if (!text || seen.has(text)) continue;
        seen.add(text);
        samples.push(text);
        if (samples.length >= max) break;
    }
    return samples;
}

/**
 * Preview texts for the Tool card: the tool name when the tool-call object
 * carries one, otherwise the whole cell as text (bare tool-name strings pass
 * through as-is).
 */
export function toolCallFieldSamples(rows, column, toolCallKey, { max = 20 } = {}) {
    if (!toolCallKey) return [];
    const samples = [];
    const seen = new Set();
    for (const message of messagesFromRows(rows, column)) {
        const raw = parseJsonMaybe(message[toolCallKey]);
        if (raw === null || raw === undefined) continue;
        const items = Array.isArray(raw) ? raw : [raw];
        for (const item of items) {
            const toolCall = parseJsonMaybe(item);
            let text;
            if (isPlainObject(toolCall)) {
                const name = toolCall.name ?? toolCall.function?.name;
                text = typeof name === 'string' && name.trim() ? name.trim() : valueToText(toolCall);
            } else {
                text = valueToText(toolCall);
            }
            if (!text || seen.has(text)) continue;
            seen.add(text);
            samples.push(text);
            if (samples.length >= max) break;
        }
        if (samples.length >= max) break;
    }
    return samples;
}

/**
 * Distinct values of `<toolCall>.<key>` across the sampled tool calls, each as
 * `{ text, isObject }` — `isObject` tells whether the value is (or parses to)
 * an object, which is what tool arguments are expected to be.
 */
export function toolCallKeySamples(rows, column, toolCallKey, key, { max = 20 } = {}) {
    if (!key) return [];
    const samples = [];
    const seen = new Set();
    for (const toolCall of toolCallObjectsFromRows(rows, column, toolCallKey)) {
        if (!(key in toolCall)) continue;
        const value = parseJsonMaybe(toolCall[key]);
        const text = valueToText(value);
        if (!text || seen.has(text)) continue;
        seen.add(text);
        samples.push({ text, isObject: isPlainObject(value) });
        if (samples.length >= max) break;
    }
    return samples;
}

/** True when a sampled tool-arguments value is not an object (Figma 4023:23405 warning). */
export function toolCallArgumentsLookInvalid(samples) {
    return (samples || []).some((sample) => sample && sample.isObject === false);
}

/**
 * Inverse view of `roleMapping` for the target-first Map Roles rows: the file
 * role values currently assigned to each canonical target, plus the ones
 * still unassigned. Values are listed in `roleValues` order.
 */
export function roleTargetsView(roleValues, roleMapping = {}) {
    const view = { unassigned: [] };
    for (const target of TRAJECTORY_ROLE_TARGETS) view[target] = [];
    for (const value of roleValues || []) {
        const target = (roleMapping || {})[value];
        if (TRAJECTORY_ROLE_TARGETS.includes(target)) view[target].push(value);
        else view.unassigned.push(value);
    }
    return view;
}

/**
 * New role mapping after the user set the file values of one target: the
 * chosen values map to `target`; values previously on that target and no
 * longer chosen become cleared (`null`); other targets are left untouched.
 */
export function assignRoleValues(roleMapping, target, selectedValues) {
    const next = { ...(roleMapping || {}) };
    const chosen = new Set(selectedValues || []);
    for (const [value, current] of Object.entries(next)) {
        if (current === target && !chosen.has(value)) next[value] = null;
    }
    for (const value of chosen) next[value] = target;
    return next;
}

/**
 * Role mapping for `roleValues`: choices already made for values still present
 * are kept (including an explicit `null` = cleared), new values auto-map on an
 * exact (case-insensitive) match with `user | assistant | tool`, anything else
 * starts unmapped (`undefined`) so Next stays blocked until the user picks.
 */
export function autoRoleMapping(roleValues, previousMapping = {}) {
    const mapping = {};
    for (const value of roleValues || []) {
        if (Object.prototype.hasOwnProperty.call(previousMapping || {}, value)) {
            mapping[value] = previousMapping[value];
            continue;
        }
        const exact = TRAJECTORY_ROLE_TARGETS.find(
            (target) => target === String(value).trim().toLowerCase()
        );
        mapping[value] = exact || undefined;
    }
    return mapping;
}

/** Merge distinct role values from two sources, keeping first-seen order. */
export function mergeRoleValues(...lists) {
    const merged = [];
    const seen = new Set();
    for (const list of lists) {
        for (const value of list || []) {
            if (value === null || value === undefined || seen.has(value)) continue;
            seen.add(value);
            merged.push(value);
        }
    }
    return merged;
}

/** Field errors for the trajectory block (empty when off or complete). */
export function trajectoryDetailsErrors(details) {
    if (!details?.hasTrajectories) return {};
    const errors = {};
    if (!details.trajectoryColumn) {
        errors.trajectoryColumn = 'Please select the field holding the trajectory messages';
    }
    if (!details.trajectoryRoleKey) {
        errors.trajectoryRoleKey = 'Please select the role field';
    }
    if (!details.trajectoryContentKey) {
        errors.trajectoryContentKey = 'Please select the content field';
    }
    if (details.trajectoryRolesLoading) {
        errors.trajectoryRoleMapping = 'Wait for the role scan to finish';
    } else if (details.trajectoryError) {
        errors.trajectoryRoleMapping = details.trajectoryError;
    } else if (details.trajectoryRoleKey) {
        const values = details.trajectoryRoleValues || [];
        const mapping = details.trajectoryRoleMapping || {};
        if (!values.length) {
            errors.trajectoryRoleMapping = 'No role values were found in the selected field';
        } else {
            const unmapped = values.filter(
                (value) => !TRAJECTORY_ROLE_TARGETS.includes(mapping[value])
            );
            if (unmapped.length) {
                errors.trajectoryRoleMapping = `Map every role to user, assistant or tool (${unmapped.join(', ')})`;
            }
        }
    }
    // Tool-call objects in the file expose keys: the tool name key must be
    // picked (arguments stay optional — the backend falls back to
    // `arguments` / `args`). Bare-string tool calls have no keys to map.
    if (
        details.trajectoryToolCallKey &&
        (details.trajectoryToolCallKeys || []).length &&
        !details.trajectoryToolCallNameKey
    ) {
        errors.trajectoryToolCallNameKey = 'Please select the key holding the tool name';
    }
    return errors;
}

/** `uploadDatasetFile({ trajectoryMapping })` payload, or null when off. */
export function trajectoryMappingFromDetails(details) {
    if (!details?.hasTrajectories || !details.trajectoryColumn) return null;
    const roleMapping = {};
    for (const [value, target] of Object.entries(details.trajectoryRoleMapping || {})) {
        if (TRAJECTORY_ROLE_TARGETS.includes(target)) roleMapping[value] = target;
    }
    const toolCallKey = details.trajectoryToolCallKey || '';
    return {
        columnName: details.trajectoryColumn,
        roleKey: details.trajectoryRoleKey || 'role',
        contentKey: details.trajectoryContentKey || 'content',
        toolCallKey,
        toolCallNameKey: toolCallKey ? details.trajectoryToolCallNameKey || '' : '',
        toolCallArgumentsKey: toolCallKey ? details.trajectoryToolCallArgumentsKey || '' : '',
        roleMapping,
    };
}

/** Rehydrate the trajectory block from a stored `DatasetInfo.column_mappings`. */
export function trajectoryDetailsFromColumnMappings(columnMappings) {
    const column = (columnMappings?.trajectory_column_name || '').trim();
    if (!column) return emptyTrajectoryDetails();
    const roleMapping = {};
    const stored = columnMappings.trajectory_role_mapping;
    if (isPlainObject(stored)) {
        for (const [value, target] of Object.entries(stored)) {
            if (TRAJECTORY_ROLE_TARGETS.includes(target)) roleMapping[value] = target;
        }
    }
    const toolCallKey = columnMappings.trajectory_tool_call_key || null;
    const nameKey = toolCallKey ? columnMappings.trajectory_tool_call_name_key || null : null;
    const argumentsKey = toolCallKey
        ? columnMappings.trajectory_tool_call_arguments_key || null
        : null;
    return {
        ...emptyTrajectoryDetails(),
        hasTrajectories: true,
        trajectoryColumn: column,
        trajectoryRoleKey: columnMappings.trajectory_role_key || 'role',
        trajectoryContentKey: columnMappings.trajectory_content_key || 'content',
        trajectoryToolCallKey: toolCallKey,
        trajectoryToolCallNameKey: nameKey,
        trajectoryToolCallArgumentsKey: argumentsKey,
        // Keep the stored picks selectable until the file sample is read again.
        trajectoryToolCallKeys: [nameKey, argumentsKey].filter(Boolean),
        trajectoryRoleValues: Object.keys(roleMapping),
        trajectoryRoleMapping: roleMapping,
    };
}
